/**
 * @file SEO.tsx
 * @summary Composant global
 *
 * Composant transverse utilisé dans plusieurs pages (header, footer, SEO, layout…).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical: string; // absolute URL
  jsonLd?: object | object[];
  ogImage?: string;
  /** Liste d'images additionnelles (og:image multiples + twitter). 1ère image = principale. */
  ogImages?: string[];
  ogType?: "website" | "article";
}

const SITE = "https://crossfitchistera.fr";
const DEFAULT_OG = `${SITE}/og-image-square.png?v=4`;

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Synchronise N balises <meta property="og:image"> (data-seo-og="1" pour cleanup). */
function setOgImages(images: string[]) {
  document.head.querySelectorAll('meta[property="og:image"][data-seo-og="1"]').forEach((n) => n.remove());
  images.forEach((url) => {
    const el = document.createElement("meta");
    el.setAttribute("property", "og:image");
    el.setAttribute("content", url);
    el.setAttribute("data-seo-og", "1");
    document.head.appendChild(el);
  });
}

export default function SEO({ title, description, canonical, jsonLd, ogImage, ogImages, ogType }: SEOProps) {
  // Auto-default ogType: blog articles → "article", everything else → "website"
  const resolvedOgType = ogType ?? (canonical.includes("/blog/") ? "article" : "website");
  const images = ogImages && ogImages.length > 0 ? ogImages : [ogImage ?? DEFAULT_OG];
  const primaryImage = images[0];
  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    setLink("canonical", canonical);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", resolvedOgType);
    // Supprime l'ancienne og:image statique (sans data-seo-og) puis injecte la liste
    const staticOg = document.head.querySelector('meta[property="og:image"]:not([data-seo-og])');
    if (staticOg) staticOg.remove();
    setOgImages(images);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", primaryImage);

    // Inject page-specific JSON-LD (tagged so we can clean up)
    const tagId = "page-jsonld";
    document.head.querySelectorAll(`script[data-seo="${tagId}"]`).forEach((s) => s.remove());
    // Also strip any orphan FAQPage script left by another route's component
    // (e.g. home FAQSection during SPA navigation) — duplicate FAQPage is a
    // Google Rich Results error.
    const willInjectFaq = jsonLd
      ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).some(
          (o: any) => o && (o["@type"] === "FAQPage" || (Array.isArray(o["@type"]) && o["@type"].includes("FAQPage"))),
        )
      : false;
    if (willInjectFaq) {
      document.head.querySelectorAll('script[type="application/ld+json"]').forEach((node) => {
        if (node.getAttribute("data-seo") === tagId) return;
        if (node.textContent?.includes('"@type":"FAQPage"') || node.textContent?.includes('"@type": "FAQPage"')) {
          node.remove();
        }
      });
    }
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.seo = tagId;
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, canonical, primaryImage, images.join("|"), resolvedOgType, JSON.stringify(jsonLd)]);


  return null;
}
